import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Package, ShoppingCart, Users, BarChart3, Settings,
  Warehouse, Receipt, Truck, ClipboardList, Search,
  TrendingUp, RefreshCcw, AlertTriangle, Store,
  CreditCard, FileText, Eye, DollarSign, LayoutDashboard,
  ArrowLeftRight, PackageCheck, Boxes, CircleDollarSign,
  Tags, Layers, Ruler, UserCog, Shield, Database,
  ArrowRightLeft, Wallet, Printer, Clock, RotateCcw,
  ChevronLeft, Plus, CheckCircle, Activity, ChevronRight,
  PieChart, Filter, Zap
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { formatCurrency, formatNumber } from '../lib/utils'

type TabType = 'overview' | 'finance' | 'inventory' | 'products' | 'admin'

interface ModuleItem {
  title: string
  description: string
  icon: any
  color: string
  bgLight: string
  path: string
  badge?: string
  category: TabType
}

const allModules: ModuleItem[] = [
  // Core / General
  {
    title: 'نقطه فروش (POS)',
    description: 'صندوق و صدور فاکتور سریع',
    icon: ShoppingCart,
    color: 'from-amber-500 to-orange-500',
    bgLight: 'bg-amber-50 dark:bg-amber-950/30',
    path: '/pos',
    category: 'overview',
  },
  {
    title: 'محصولات',
    description: 'مدیریت و تعریف کالاها',
    icon: Package,
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50 dark:bg-emerald-950/30',
    path: '/inventory/products',
    category: 'overview',
  },
  {
    title: 'مشتریان',
    description: 'باشگاه و مدیریت مشتریان',
    icon: Users,
    color: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50 dark:bg-violet-950/30',
    path: '/customers',
    category: 'overview',
  },
  {
    title: 'گزارشات فروش',
    description: 'تحلیل آمار و نمودارهای مالی',
    icon: BarChart3,
    color: 'from-sky-500 to-blue-600',
    bgLight: 'bg-sky-50 dark:bg-sky-950/30',
    path: '/sales/reports',
    category: 'overview',
  },
  {
    title: 'تنظیمات سیستم',
    description: 'پروفایل و پیکربندی سیستم',
    icon: Settings,
    color: 'from-slate-500 to-gray-600',
    bgLight: 'bg-slate-50 dark:bg-slate-950/30',
    path: '/settings',
    category: 'overview',
  },

  // Finance & Sales
  {
    title: 'تاریخچه فروش',
    description: 'فاکتورها و فروش‌های انجام شده',
    icon: ClipboardList,
    color: 'from-rose-500 to-pink-600',
    bgLight: 'bg-rose-50 dark:bg-rose-950/30',
    path: '/sales/history',
    category: 'finance',
  },
  {
    title: 'مدیریت پرداخت‌ها',
    description: 'تراکنش‌ها و روش‌های دریافت',
    icon: Wallet,
    color: 'from-green-500 to-emerald-600',
    bgLight: 'bg-green-50 dark:bg-green-950/30',
    path: '/sales/payments',
    category: 'finance',
  },
  {
    title: 'صورتحساب خرید',
    description: 'فاکتورهای خرید از تامین‌کننده',
    icon: Receipt,
    color: 'from-fuchsia-500 to-pink-600',
    bgLight: 'bg-fuchsia-50 dark:bg-fuchsia-950/30',
    path: '/purchase/bills',
    category: 'finance',
  },
  {
    title: 'تامین‌کنندگان',
    description: 'لیست و حساب تامین‌کنندگان',
    icon: Store,
    color: 'from-orange-500 to-red-500',
    bgLight: 'bg-orange-50 dark:bg-orange-950/30',
    path: '/purchase/suppliers',
    category: 'finance',
  },
  {
    title: 'سفارشات خرید (PO)',
    description: 'صدور و پیگیری سفارش خرید',
    icon: FileText,
    color: 'from-indigo-500 to-blue-600',
    bgLight: 'bg-indigo-50 dark:bg-indigo-950/30',
    path: '/purchase/orders',
    category: 'finance',
  },

  // Inventory & Warehouse
  {
    title: 'مدیریت انبارها',
    description: 'لیست و ظرفیت انبارها',
    icon: Warehouse,
    color: 'from-teal-500 to-cyan-600',
    bgLight: 'bg-teal-50 dark:bg-teal-950/30',
    path: '/inventory/warehouses',
    category: 'inventory',
  },
  {
    title: 'دریافت کالا',
    description: 'ثبت ورود کالا به انبار',
    icon: PackageCheck,
    color: 'from-blue-500 to-indigo-600',
    bgLight: 'bg-blue-50 dark:bg-blue-950/30',
    path: '/inventory/receive',
    category: 'inventory',
  },
  {
    title: 'انتقال بین انبارها',
    description: 'جابجایی کالا بین انبارها',
    icon: ArrowLeftRight,
    color: 'from-cyan-500 to-teal-500',
    bgLight: 'bg-cyan-50 dark:bg-cyan-950/30',
    path: '/inventory/transfers',
    category: 'inventory',
  },
  {
    title: 'تعدیل موجودی',
    description: 'اصلاح و انبارگردانی سریع',
    icon: RefreshCcw,
    color: 'from-lime-500 to-green-600',
    bgLight: 'bg-lime-50 dark:bg-lime-950/30',
    path: '/inventory/adjustments',
    category: 'inventory',
  },
  {
    title: 'موجودی کم (هشدار)',
    description: 'کالاهای به نقطه سفارش رسیده',
    icon: AlertTriangle,
    color: 'from-red-500 to-rose-600',
    bgLight: 'bg-red-50 dark:bg-red-950/30',
    path: '/inventory/low-stock',
    badge: '۲۳',
    category: 'inventory',
  },
  {
    title: 'شمارش دوره‌ای',
    description: 'انبارگردانی و شمارش فیزیکی',
    icon: Search,
    color: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50 dark:bg-violet-950/30',
    path: '/inventory/cycle-counts',
    category: 'inventory',
  },
  {
    title: 'انبار مرکزی',
    description: 'مشاهده کل موجودی کالاها',
    icon: Database,
    color: 'from-sky-500 to-blue-600',
    bgLight: 'bg-sky-50 dark:bg-sky-950/30',
    path: '/inventory/stock',
    category: 'inventory',
  },
  {
    title: 'تاریخچه حرکات',
    description: 'ریز ورود و خروج کالاها',
    icon: ArrowRightLeft,
    color: 'from-indigo-500 to-violet-600',
    bgLight: 'bg-indigo-50 dark:bg-indigo-950/30',
    path: '/inventory/movements',
    category: 'inventory',
  },

  // Products & Catalog
  {
    title: 'دسته‌بندی‌ها',
    description: 'مدیریت ساختار و گروه‌بندی',
    icon: Layers,
    color: 'from-pink-500 to-rose-600',
    bgLight: 'bg-pink-50 dark:bg-pink-950/30',
    path: '/products/categories',
    category: 'products',
  },
  {
    title: 'برندها و سازندگان',
    description: 'مدیریت برندهای کالا',
    icon: Tags,
    color: 'from-amber-500 to-yellow-600',
    bgLight: 'bg-amber-50 dark:bg-amber-950/30',
    path: '/products/brands',
    category: 'products',
  },
  {
    title: 'واحدهای اندازه‌گیری',
    description: 'کیلوگرم، عدد، بسته و ...',
    icon: Ruler,
    color: 'from-teal-500 to-green-600',
    bgLight: 'bg-teal-50 dark:bg-teal-950/30',
    path: '/products/units',
    category: 'products',
  },

  // Admin & System
  {
    title: 'کاربران و پرسنل',
    description: 'مدیریت کاربران سیستم',
    icon: UserCog,
    color: 'from-gray-500 to-slate-600',
    bgLight: 'bg-gray-50 dark:bg-gray-950/30',
    path: '/admin/users',
    category: 'admin',
  },
  {
    title: 'نقش‌ها و دسترسی‌ها',
    description: 'تعیین سطوح دسترسی',
    icon: Shield,
    color: 'from-red-500 to-orange-600',
    bgLight: 'bg-red-50 dark:bg-red-950/30',
    path: '/admin/roles',
    category: 'admin',
  },
  {
    title: 'چاپ رسید',
    description: 'تنظیمات پرینتر و قالب رسید',
    icon: Printer,
    color: 'from-neutral-500 to-gray-600',
    bgLight: 'bg-neutral-50 dark:bg-neutral-950/30',
    path: '/pos/receipts',
    category: 'admin',
  },
  {
    title: 'شیفت‌های کاری',
    description: 'مدیریت و گزارش شیفت صندوق‌داران',
    icon: Clock,
    color: 'from-orange-500 to-amber-600',
    bgLight: 'bg-orange-50 dark:bg-orange-950/30',
    path: '/pos/shifts',
    category: 'admin',
  },
]

export default function Dashboard() {
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [searchQuery, setSearchQuery] = useState('')

  const tabs = [
    { id: 'overview', label: 'نمای کلی و دسترسی سریع', icon: LayoutDashboard },
    { id: 'finance', label: 'فروش و مالی', icon: CircleDollarSign },
    { id: 'inventory', label: 'انبار و موجودی', icon: Warehouse, badge: '۲۳' },
    { id: 'products', label: 'محصولات و کاتالوگ', icon: Package },
    { id: 'admin', label: 'مدیریت و سیستم', icon: Shield },
  ] as const

  // Filtered modules based on search or active tab
  const displayedModules = searchQuery.trim()
    ? allModules.filter(m =>
        m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allModules.filter(m => activeTab === 'overview' ? true : m.category === activeTab)

  return (
    <div className="min-h-screen bg-neutral-50/50 dark:bg-neutral-950 p-4 sm:p-6 space-y-6">
      {/* Header Container */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                داشبورد مدیریتی کسب
              </h1>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
              {user?.full_name ? `خوش آمدید، ${user.full_name}` : 'سیستم جامع مدیریت انبارداری و فروش'}
            </p>
          </div>

          {/* Search bar */}
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute right-3.5 top-3 text-neutral-400" />
            <input
              type="text"
              placeholder="جستجوی سریع ماژول یا ابزار..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-3 pr-10 py-2 rounded-xl text-sm border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-2.5 text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
              >
                پاک کردن
              </button>
            )}
          </div>
        </div>

        {/* Modular Tabs Navigation */}
        {!searchQuery && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1.5 pt-2 scrollbar-none border-t border-neutral-100 dark:border-neutral-800/60 min-w-0 w-full">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`flex items-center gap-2 px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap shrink-0 relative select-none ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/20'
                      : 'bg-neutral-100 dark:bg-neutral-800/60 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200/80 dark:hover:bg-neutral-800'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="whitespace-nowrap">{tab.label}</span>
                  {tab.badge && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full shrink-0 ${
                      isActive ? 'bg-white text-primary-600' : 'bg-red-500 text-white'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Tab Context Container */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab + (searchQuery ? '-search' : '')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="space-y-6"
        >
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && !searchQuery && (
            <div className="space-y-6">
              {/* Contextual KPI Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 border border-neutral-200/80 dark:border-neutral-800 flex items-center justify-between shadow-sm">
                  <div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">فروش امروز</p>
                    <p className="text-xl font-bold text-neutral-900 dark:text-white mt-1">
                      {formatCurrency(12450000)}
                    </p>
                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3" /> +۱۴٪ نسبت به دیروز
                    </span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                    <CircleDollarSign className="w-6 h-6" />
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 border border-neutral-200/80 dark:border-neutral-800 flex items-center justify-between shadow-sm">
                  <div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">هشدار موجودی کم</p>
                    <p className="text-xl font-bold text-neutral-900 dark:text-white mt-1">۲۳ کالا</p>
                    <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1 mt-1">
                      <AlertTriangle className="w-3 h-3" /> نیاز به سفارش مجدد
                    </span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 border border-neutral-200/80 dark:border-neutral-800 flex items-center justify-between shadow-sm">
                  <div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">سفارشات در انتظار</p>
                    <p className="text-xl font-bold text-neutral-900 dark:text-white mt-1">۷ سفارش</p>
                    <span className="text-[11px] text-blue-600 dark:text-blue-400 font-medium flex items-center gap-1 mt-1">
                      <Truck className="w-3 h-3" /> تامین‌کنندگان فعال
                    </span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Truck className="w-6 h-6" />
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 border border-neutral-200/80 dark:border-neutral-800 flex items-center justify-between shadow-sm">
                  <div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">کل کالاهای فعال</p>
                    <p className="text-xl font-bold text-neutral-900 dark:text-white mt-1">۱,۲۳۴ قلم</p>
                    <span className="text-[11px] text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1 mt-1">
                      <Boxes className="w-3 h-3" /> در ۳ انبار اصلی
                    </span>
                  </div>
                  <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                    <Package className="w-6 h-6" />
                  </div>
                </div>
              </div>

              {/* Bento Grid: Quick Launch + System Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Launchpad Grid */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      ماژول‌های دسترسی سریع
                    </h2>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                      همه بخش‌های سیستم
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {displayedModules.slice(0, 12).map((mod) => (
                      <Link key={mod.path} to={mod.path} className="group block">
                        <div className={`${mod.bgLight} rounded-2xl p-3.5 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 group-hover:shadow-md border border-neutral-200/40 dark:border-neutral-800/40 relative`}>
                          {mod.badge && (
                            <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                              {mod.badge}
                            </div>
                          )}
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-sm`}>
                            <mod.icon className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200 leading-tight">
                            {mod.title}
                          </span>
                          <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-1">
                            {mod.description}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Side Activity Timeline & Quick Actions */}
                <div className="space-y-4">
                  <h2 className="text-base font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary-500" />
                    فعالیت‌های اخیر سیستم
                  </h2>

                  <div className="bg-white dark:bg-neutral-900 rounded-2xl p-4 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-3">
                    {[
                      { title: 'ثبت دریافت کالا', desc: '۵۰ عدد سامسونگ A54 - انبار مرکزی', time: '۱۰ دقیقه پیش', type: 'success' },
                      { title: 'فروش موفق POS', desc: 'فاکتور #۱۰۸۴ به مبلغ ۴۵۰,۰۰۰ تومان', time: '۲۵ دقیقه پیش', type: 'primary' },
                      { title: 'انتقال موجودی', desc: '۱۰ عدد هدفون - انبار مرکزی به جنوبی', time: '۱ ساعت پیش', type: 'info' },
                      { title: 'هشدار موجودی', desc: 'کالای چیپس ذرتی به کمتر از حداقل رسید', time: '۲ ساعت پیش', type: 'warning' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${
                          item.type === 'success' ? 'bg-emerald-500' :
                          item.type === 'primary' ? 'bg-primary-500' :
                          item.type === 'info' ? 'bg-cyan-500' : 'bg-amber-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-neutral-800 dark:text-neutral-200">{item.title}</p>
                            <span className="text-[10px] text-neutral-400">{item.time}</span>
                          </div>
                          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}

                    <Link to="/inventory/movements" className="block text-center text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline pt-2">
                      مشاهده کل رویدادها
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: FINANCE & SALES */}
          {activeTab === 'finance' && !searchQuery && (
            <div className="space-y-6">
              {/* Financial Dashboard Container */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                      <CircleDollarSign className="w-5 h-5 text-emerald-500" />
                      داشبورد اختصاصی فروش و تراکنش‌های مالی
                    </h2>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      مدیریت فاکتورها، پرداخت‌ها، صورتحساب‌های خرید و تامین‌کنندگان
                    </p>
                  </div>
                  <Link to="/pos" className="px-3.5 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:bg-emerald-700 transition-colors flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> ثبت فاکتور جدید
                  </Link>
                </div>

                {/* Financial KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-100 dark:border-emerald-900/40">
                    <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">مجموع دریافتی‌های ماه</p>
                    <p className="text-2xl font-black text-emerald-900 dark:text-emerald-100 mt-1">
                      {formatCurrency(148500000)}
                    </p>
                    <span className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-2 block">
                      ۱۲۸ فاکتور صادر شده
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/20 border border-rose-100 dark:border-rose-900/40">
                    <p className="text-xs font-medium text-rose-800 dark:text-rose-300">صورتحساب‌های پرداخت نشده (خرید)</p>
                    <p className="text-2xl font-black text-rose-900 dark:text-rose-100 mt-1">
                      {formatCurrency(34200000)}
                    </p>
                    <span className="text-[11px] text-rose-700 dark:text-rose-400 mt-2 block">
                      ۴ بدهی تامین‌کننده
                    </span>
                  </div>

                  <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20 border border-blue-100 dark:border-blue-900/40">
                    <p className="text-xs font-medium text-blue-800 dark:text-blue-300">میانگین مبلغ هر فاکتور</p>
                    <p className="text-2xl font-black text-blue-900 dark:text-blue-100 mt-1">
                      {formatCurrency(1160000)}
                    </p>
                    <span className="text-[11px] text-blue-700 dark:text-blue-400 mt-2 block">
                      نرخ رشد +۸٪
                    </span>
                  </div>
                </div>

                {/* Specialized Sales Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {allModules.filter(m => m.category === 'finance').map((mod) => (
                    <Link key={mod.path} to={mod.path} className="group block">
                      <div className={`${mod.bgLight} rounded-2xl p-4 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 border border-neutral-200/50 dark:border-neutral-800/50`}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 shadow-md`}>
                          <mod.icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200">
                          {mod.title}
                        </span>
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                          {mod.description}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: INVENTORY & WAREHOUSES */}
          {activeTab === 'inventory' && !searchQuery && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Warehouse className="w-5 h-5 text-teal-500" />
                      داشبورد مدیریت انبارها و موجودی
                    </h2>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      نظارت بر ظرفیت انبارها، دریافت کالا، جابجایی و اخطارهای موجودی
                    </p>
                  </div>
                  <Link to="/inventory/receive" className="px-3.5 py-2 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 transition-colors flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> ثبت دریافت کالا
                  </Link>
                </div>

                {/* Warehouse Capacity Gauges */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: 'انبار مرکزی (تهران)', code: 'WH-MAIN', capacity: 85, items: 12450, status: 'پر' },
                    { name: 'انبار شمالی (تبریز)', code: 'WH-NORTH', capacity: 45, items: 3200, status: 'عادی' },
                    { name: 'انبار جنوبی (شیراز)', code: 'WH-SOUTH', capacity: 72, items: 5680, status: 'نسبتا پر' },
                  ].map((wh, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200/60 dark:border-neutral-700/60 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-neutral-900 dark:text-white">{wh.name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          wh.capacity > 80 ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                        }`}>
                          {wh.status}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-[11px] text-neutral-500">
                          <span>ظرفیت اشغال شده</span>
                          <span className="font-bold text-neutral-800 dark:text-neutral-200">{wh.capacity}٪</span>
                        </div>
                        <div className="w-full bg-neutral-200 dark:bg-neutral-700 h-2 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${wh.capacity > 80 ? 'bg-red-500' : 'bg-teal-500'}`} style={{ width: `${wh.capacity}%` }} />
                        </div>
                      </div>
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
                        مجموع کالاها: {formatNumber(wh.items)} عدد
                      </p>
                    </div>
                  ))}
                </div>

                {/* Inventory Specialized Modules */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {allModules.filter(m => m.category === 'inventory').map((mod) => (
                    <Link key={mod.path} to={mod.path} className="group block">
                      <div className={`${mod.bgLight} rounded-2xl p-4 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 border border-neutral-200/50 dark:border-neutral-800/50 relative`}>
                        {mod.badge && (
                          <div className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                            {mod.badge}
                          </div>
                        )}
                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 shadow-md`}>
                          <mod.icon className="w-5.5 h-5.5 text-white" />
                        </div>
                        <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200">
                          {mod.title}
                        </span>
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                          {mod.description}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: PRODUCTS & CATALOG */}
          {activeTab === 'products' && !searchQuery && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Package className="w-5 h-5 text-purple-500" />
                      داشبورد کاتالوگ و دسته‌بندی محصولات
                    </h2>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      تعریف کالاها، گروه‌بندی دسته‌ها، برندها و واحدهای اندازه‌گیری
                    </p>
                  </div>
                  <Link to="/inventory/products/new" className="px-3.5 py-2 bg-purple-600 text-white rounded-xl text-xs font-bold hover:bg-purple-700 transition-colors flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> تعریف محصول جدید
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {allModules.filter(m => m.category === 'products' || m.path === '/inventory/products').map((mod) => (
                    <Link key={mod.path} to={mod.path} className="group block">
                      <div className={`${mod.bgLight} rounded-2xl p-4 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 border border-neutral-200/50 dark:border-neutral-800/50`}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 shadow-md`}>
                          <mod.icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200">
                          {mod.title}
                        </span>
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                          {mod.description}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: ADMIN & SYSTEM */}
          {activeTab === 'admin' && !searchQuery && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-6">
                <div className="flex items-center justify-between border-b border-neutral-100 dark:border-neutral-800 pb-4">
                  <div>
                    <h2 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                      <Shield className="w-5 h-5 text-gray-500" />
                      داشبورد مدیریت سیستم و کاربران
                    </h2>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      کنترل دسترسی‌ها، پرسنل، شیفت‌های کاری و چاپ رسید
                    </p>
                  </div>
                  <Link to="/settings" className="px-3.5 py-2 bg-neutral-700 text-white rounded-xl text-xs font-bold hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
                    <Settings className="w-4 h-4" /> تنظیمات کلی
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {allModules.filter(m => m.category === 'admin').map((mod) => (
                    <Link key={mod.path} to={mod.path} className="group block">
                      <div className={`${mod.bgLight} rounded-2xl p-4 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 border border-neutral-200/50 dark:border-neutral-800/50`}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 shadow-md`}>
                          <mod.icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200">
                          {mod.title}
                        </span>
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                          {mod.description}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SEARCH RESULTS VIEW */}
          {searchQuery && (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-5 border border-neutral-200/80 dark:border-neutral-800 shadow-sm space-y-4">
              <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                نتایج جستجو برای "{searchQuery}": ({displayedModules.length} ماژول)
              </p>

              {displayedModules.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm">ماژولی با این عبارت پیدا نشد.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {displayedModules.map((mod) => (
                    <Link key={mod.path} to={mod.path} className="group block">
                      <div className={`${mod.bgLight} rounded-2xl p-4 flex flex-col items-center justify-center text-center aspect-square transition-all duration-200 group-hover:scale-105 border border-neutral-200/50 dark:border-neutral-800/50`}>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${mod.color} flex items-center justify-center mb-2 shadow-md`}>
                          <mod.icon className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xs font-bold text-neutral-800 dark:text-neutral-200">
                          {mod.title}
                        </span>
                        <span className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                          {mod.description}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
